import { useQuery } from "react-query";
import {
  RegressionListResponse,
  RegressionRunResults,
  RegressionRunSummary,
  RegressionSuiteSummary,
  RegressionTestRow,
  RegressionConversationTurn,
  RegressionEvaluationResult,
} from "./types";

const BASE_URL = "https://agent-labs-eight.vercel.app";
const API_KEY = "alk_57e4a2aad1a2b2be856308bd8dec01588bf89217";
const ARTLIST_AGENT_IDS = [
  "69dfd081e6f6b517ad565fab",
  "69e7eb1c9a3d9f72d9f24129",
  "69dfcfd63a23224ce1267c63",
  "69e7e33a079788894ad37461",
  "69dfcf757f15e97f4011e0a8",
  "69dfcea67f15e97f4011e045",
  "69dfd0c37f15e97f4011e18e",
  "69dfd0f93a23224ce1267d3e",
  "69dfd11c3a23224ce1267d6d",
  "69d796463a23224ce1252c41",
  "69df7f307f15e97f4011cdfa",
  "69d60fb6278b5c7f46100600",
  "69dfb5977f15e97f4011d856",
  "69dfd02e7f15e97f4011e125",
  "69dfd1753a23224ce1267db2",
];

interface AgentLabDataset {
  _id?: string;
  id?: string;
  datasetId?: string;
  name: string;
  type?: "UAT" | "CAT";
  agentId?: string;
  description?: string;
}

interface AgentLabRun {
  _id?: string;
  id?: string;
  runId?: string;
  experimentId?: string;
  experimentName?: string;
  datasetId?: string;
  status?: "running" | "succeeded" | "failed" | "cancelled" | string;
  startedAt?: string;
  finishedAt?: string;
  scores?: Record<string, unknown>;
  metrics?: Record<string, unknown>;
  results?: { perTest?: Array<Record<string, unknown>> };
}

async function agentLabFetch<T>(path: string): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${API_KEY}`,
      "x-api-key": API_KEY,
    },
  });
  if (!res.ok) {
    throw new Error(`Simulation tests request failed: ${res.status} ${url}`);
  }
  return (await res.json()) as T;
}

function runId(run: AgentLabRun): string {
  return run.runId ?? run.id ?? run._id ?? "";
}

function datasetId(dataset: AgentLabDataset): string {
  return dataset.datasetId ?? dataset.id ?? dataset._id ?? "";
}

function adaptRunSummary(run: AgentLabRun): RegressionRunSummary {
  const startedAt = run.startedAt ? new Date(run.startedAt).getTime() : 0;
  const finishedAt = run.finishedAt ? new Date(run.finishedAt).getTime() : 0;
  return {
    id: runId(run),
    name: run.startedAt ?? runId(run),
    startedAt: run.startedAt,
    finishedAt: run.finishedAt,
    status: run.status,
    mtimeMs: finishedAt || startedAt,
    hasResults: run.status === "succeeded" || !!run.results?.perTest?.length,
  };
}

function adaptSuite(
  dataset: AgentLabDataset,
  runs: AgentLabRun[]
): RegressionSuiteSummary {
  return {
    id: datasetId(dataset),
    name: dataset.name,
    runs: runs
      .map(adaptRunSummary)
      .sort((a, b) => (b.mtimeMs ?? 0) - (a.mtimeMs ?? 0)),
  };
}

interface AgentLabEvaluation {
  criterion?: string;
  category?: string;
  weight?: number;
  kind?: string;
  passed?: boolean;
  pass?: boolean;
  reason?: string;
  reasoning?: string;
  score?: number;
}

interface AgentLabTranscriptTurn {
  user?: string;
  agent?: string;
  interactionId?: string;
  timestamp?: string;
  escalated?: boolean;
  escalatedDueToError?: boolean;
}

function adaptEvaluations(
  evals: AgentLabEvaluation[] | undefined
): RegressionEvaluationResult[] {
  if (!evals || !Array.isArray(evals)) return [];
  return evals.map((e) => ({
    criterion: e.criterion,
    category: e.category,
    weight: e.weight,
    pass: e.passed ?? e.pass,
    passed: e.passed ?? e.pass,
    score: e.score,
    reasoning: e.reasoning ?? e.reason,
    kind: e.kind,
  }));
}

function adaptTranscript(
  raw: unknown
): {
  turns: RegressionConversationTurn[];
  escalated: boolean;
  escalatedAtTurn: number | undefined;
  escalatedDueToError: boolean;
} {
  if (!Array.isArray(raw)) {
    return {
      turns: [],
      escalated: false,
      escalatedAtTurn: undefined,
      escalatedDueToError: false,
    };
  }
  const turns: RegressionConversationTurn[] = [];
  let escalated = false;
  let escalatedAtTurn: number | undefined;
  let escalatedDueToError = false;

  raw.forEach((entry, idx) => {
    if (!entry || typeof entry !== "object") return;
    const t = entry as AgentLabTranscriptTurn & { role?: string; content?: string };
    if (typeof t.role === "string" && typeof t.content === "string") {
      turns.push({ role: t.role, content: t.content, timestamp: t.timestamp });
      return;
    }
    if (typeof t.user === "string" && t.user.length > 0) {
      turns.push({ role: "user", content: t.user, timestamp: t.timestamp, turnIndex: idx });
    }
    if (typeof t.agent === "string" && t.agent.length > 0) {
      turns.push({
        role: "agent",
        content: t.agent,
        timestamp: t.timestamp,
        turnIndex: idx,
        escalated: t.escalated,
        escalatedDueToError: t.escalatedDueToError,
      });
    }
    if (t.escalated) {
      escalated = true;
      if (escalatedAtTurn == null) escalatedAtTurn = idx;
      if (t.escalatedDueToError) escalatedDueToError = true;
    }
  });

  return { turns, escalated, escalatedAtTurn, escalatedDueToError };
}

function adaptPerTest(
  raw: Record<string, unknown>,
  index: number,
  userInfoByName?: Map<string, Record<string, unknown>>
): RegressionTestRow {
  const get = <T>(...keys: string[]): T | undefined => {
    for (const k of keys) {
      const v = raw[k];
      if (v !== undefined && v !== null) return v as T;
    }
    return undefined;
  };

  const evaluations = adaptEvaluations(
    get<AgentLabEvaluation[]>("evaluations", "evaluationResults", "criteria")
  );

  const transcriptRaw =
    get<unknown>("transcript", "conversation", "messages", "turns") ?? [];
  const { turns, escalated, escalatedAtTurn, escalatedDueToError } =
    adaptTranscript(transcriptRaw);

  const passedFlag = get<boolean>("passed");
  const overall = get<number>("overall");
  const llmJudge = get<number>("llmJudge");
  const deterministic = get<number>("deterministic");
  const explicitScore = get<number>("overallScore", "score", "scorePercent");

  let score: number;
  if (explicitScore != null) {
    score = Number(explicitScore);
  } else if (overall != null) {
    score = Math.round(Number(overall) * 100);
  } else if (llmJudge != null) {
    score = Math.round(Number(llmJudge) * 100);
  } else if (deterministic != null) {
    score = Math.round(Number(deterministic) * 100);
  } else {
    score = passedFlag ? 100 : 0;
  }

  const passedCount = evaluations.filter(
    (r) => r.pass ?? r.passed ?? false
  ).length;
  const passRate =
    get<string>("passRate") ??
    (evaluations.length > 0 ? `${passedCount}/${evaluations.length}` : undefined);

  const duration = Number(
    get<number>("duration", "durationMs", "durationMillis") ?? 0
  );

  const testName = String(
    get("testName", "name", "title") ?? `Test ${index + 1}`
  );
  const embeddedUserInfo = get<Record<string, unknown>>(
    "userInfo",
    "agentVariables",
    "agent_variables"
  );
  const userInfo =
    embeddedUserInfo ?? userInfoByName?.get(testName) ?? undefined;

  return {
    testName,
    taskId: String(
      get("taskId", "testId", "id", "_id") ??
        get("name") ??
        `test-${index}`
    ),
    userInfo,
    conversation: turns,
    evaluationResults: evaluations,
    overallScore: score,
    passRate,
    duration,
    escalated: get<boolean>("escalated") ?? escalated,
    escalatedAtTurn: get<number>("escalatedAtTurn") ?? escalatedAtTurn,
    escalatedDueToError:
      get<boolean>("escalatedDueToError") ?? escalatedDueToError,
  };
}

function adaptRunResults(
  run: AgentLabRun,
  datasetName?: string,
  userInfoByName?: Map<string, Record<string, unknown>>
): RegressionRunResults {
  const perTest = run.results?.perTest ?? [];
  const tests = perTest.map((t, i) => adaptPerTest(t, i, userInfoByName));
  const totalPass = tests.filter((t) => (t.overallScore ?? 0) >= 100).length;
  const aggregateScore =
    tests.length > 0
      ? tests.reduce((acc, t) => acc + (t.overallScore ?? 0), 0) / tests.length
      : 0;
  return {
    runId: runId(run),
    suiteName: datasetName ?? run.experimentName ?? "Run",
    agentId: undefined,
    runDate: run.startedAt,
    status: run.status,
    tests,
    aggregateScore,
    totalPass,
    totalFail: tests.length - totalPass,
  };
}

export function useRegressionList() {
  return useQuery<RegressionListResponse>({
    queryKey: ["regression-results-v2", "list"],
    queryFn: async () => {
      const datasetsByAgent = await Promise.all(
        ARTLIST_AGENT_IDS.map(async (agentId) => {
          try {
            const { datasets = [] } = await agentLabFetch<{
              datasets: AgentLabDataset[];
            }>(
              `/api/datasets?agentId=${encodeURIComponent(agentId)}&type=UAT`
            );
            return datasets;
          } catch {
            return [] as AgentLabDataset[];
          }
        })
      );
      const seen = new Set<string>();
      const datasets = datasetsByAgent.flat().filter((d) => {
        const id = datasetId(d);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      const suites = await Promise.all(
        datasets.map(async (d) => {
          const id = datasetId(d);
          if (!id) return adaptSuite(d, []);
          try {
            const { runs } = await agentLabFetch<{ runs: AgentLabRun[] }>(
              `/api/datasets/${encodeURIComponent(id)}/runs`
            );
            return adaptSuite(d, runs ?? []);
          } catch {
            return adaptSuite(d, []);
          }
        })
      );
      return { rootDir: "simulation-tests", suites };
    },
  });
}

interface AgentLabDatasetTest {
  name?: string;
  userInfo?: Record<string, unknown>;
}

interface AgentLabDatasetDetail extends AgentLabDataset {
  suite?: {
    tests?: AgentLabDatasetTest[];
  };
}

export function useRegressionRun(
  datasetId: string | null,
  runIdValue: string | null,
  datasetName?: string
) {
  return useQuery<RegressionRunResults>({
    queryKey: ["regression-results-v2", "run", datasetId, runIdValue],
    enabled: !!runIdValue,
    refetchInterval: (data) => (data?.status === "running" ? 10_000 : false),
    queryFn: async () => {
      const { run } = await agentLabFetch<{ run: AgentLabRun }>(
        `/api/runs/${encodeURIComponent(runIdValue!)}`
      );
      const effectiveDatasetId = datasetId ?? run.datasetId;
      let userInfoByName: Map<string, Record<string, unknown>> | undefined;
      if (effectiveDatasetId) {
        try {
          const { dataset } = await agentLabFetch<{
            dataset: AgentLabDatasetDetail;
          }>(`/api/datasets/${encodeURIComponent(effectiveDatasetId)}`);
          const tests = dataset?.suite?.tests ?? [];
          userInfoByName = new Map();
          for (const t of tests) {
            if (t.name && t.userInfo) userInfoByName.set(t.name, t.userInfo);
          }
        } catch {
          userInfoByName = undefined;
        }
      }
      return adaptRunResults(run, datasetName, userInfoByName);
    },
  });
}
